import { ISSUE_RESPONSIBILITY, IssuesCasesMap } from '@img-arena/consoles-api-streaming'

export const SCOPE_LABEL_MAP = {
  [ISSUE_RESPONSIBILITY.EE]: 'EE',
  [ISSUE_RESPONSIBILITY.MCR]: 'MCR',
  [ISSUE_RESPONSIBILITY.UPSTREAM]: 'Upstream',
  [ISSUE_RESPONSIBILITY.TECHNICAL]: 'Technical',
  [ISSUE_RESPONSIBILITY.DELIVERY]: 'Delivery',
  [ISSUE_RESPONSIBILITY.IT]: 'IT',
  [ISSUE_RESPONSIBILITY.ADMIN]: 'Admin',
  [ISSUE_RESPONSIBILITY.NEULION]: 'Neulion',
  [ISSUE_RESPONSIBILITY.OTHER]: 'Other',
  [ISSUE_RESPONSIBILITY.PRODUCTIONPARTNERS]: 'Production Partners'
} as const satisfies Record<ValueOf<typeof ISSUE_RESPONSIBILITY>, string>

export const DEFAULT_CASE = {
  duration: ''
} as const

export const CASES_BY_SCOPE = {
  [ISSUE_RESPONSIBILITY.EE]: {
    lateStreamStart: DEFAULT_CASE,
    lateSlateDrop: DEFAULT_CASE,
    incorrectEncoder: DEFAULT_CASE,
    incorrectSlate: DEFAULT_CASE,
    EEOther: DEFAULT_CASE
  },
  [ISSUE_RESPONSIBILITY.MCR]: {
    hitOnPictures: DEFAULT_CASE,
    hitOnAudio: DEFAULT_CASE,
    lossOfFeed: DEFAULT_CASE,
    switchingError: DEFAULT_CASE,
    technicalFailure: DEFAULT_CASE,
    encryptError: DEFAULT_CASE,
    powerOutage: DEFAULT_CASE,
    weatherIssue: DEFAULT_CASE,
    upconverterUnavailable: DEFAULT_CASE,
    MCROther: DEFAULT_CASE
  },
  [ISSUE_RESPONSIBILITY.UPSTREAM]: {
    hitOnPictures: DEFAULT_CASE,
    hitOnAudio: DEFAULT_CASE,
    noBroadcastersGFX: DEFAULT_CASE,
    noBroadcastersClock: DEFAULT_CASE,
    lossOfFeed: DEFAULT_CASE,
    externalSwitchingError: DEFAULT_CASE,
    technicalFailure: DEFAULT_CASE,
    encryptError: DEFAULT_CASE,
    lowPower: DEFAULT_CASE,
    powerOutage: DEFAULT_CASE,
    audioHot: DEFAULT_CASE,
    eventCanceled: DEFAULT_CASE,
    weatherDelay: DEFAULT_CASE,
    otherDelay: DEFAULT_CASE,
    ingestOther: DEFAULT_CASE
  },
  [ISSUE_RESPONSIBILITY.TECHNICAL]: {
    bmMultiview: DEFAULT_CASE,
    vgaMonitors: DEFAULT_CASE,
    audioMonitor: DEFAULT_CASE,
    kvm: DEFAULT_CASE,
    bncs: DEFAULT_CASE,
    powerOutage: DEFAULT_CASE,
    hdMonitor: DEFAULT_CASE,
    videoHub: DEFAULT_CASE,
    technicalOther: DEFAULT_CASE
  },
  [ISSUE_RESPONSIBILITY.DELIVERY]: {
    streamRestart: DEFAULT_CASE,
    encoderCrash: DEFAULT_CASE,
    encoderFreeze: DEFAULT_CASE,
    studioCrash: DEFAULT_CASE,
    studioFreeze: DEFAULT_CASE,
    windowsCrash: DEFAULT_CASE,
    windowsFreeze: DEFAULT_CASE,
    platformIssue: DEFAULT_CASE,
    platformDown: DEFAULT_CASE,
    blackMagicCard: DEFAULT_CASE,
    zixiUnavailable: DEFAULT_CASE,
    eventIDCorrupt: DEFAULT_CASE,
    cdn: DEFAULT_CASE,
    deliveryOther: DEFAULT_CASE
  },
  [ISSUE_RESPONSIBILITY.IT]: {
    broadcastLossOfNetwork: DEFAULT_CASE,
    broadcastNetworkDrop: DEFAULT_CASE,
    broadcastBandwidthLow: DEFAULT_CASE,
    packetLoss: DEFAULT_CASE,
    broadcastOther: DEFAULT_CASE
  },
  [ISSUE_RESPONSIBILITY.ADMIN]: {
    incorrectBookingLength: DEFAULT_CASE,
    noEventBookingMade: DEFAULT_CASE,
    incorrectAudioChannels: DEFAULT_CASE,
    noUpconverterBooked: DEFAULT_CASE,
    fiberBookingError: DEFAULT_CASE,
    satelliteBookingError: DEFAULT_CASE,
    incorrectEncoderBooking: DEFAULT_CASE,
    adminOther: DEFAULT_CASE
  },
  [ISSUE_RESPONSIBILITY.NEULION]: {
    neulionLateSlateDrop: DEFAULT_CASE,
    neulionIncorrectSlate: DEFAULT_CASE,
    neulionNetworkIssue: DEFAULT_CASE,
    neulionCablingIssue: DEFAULT_CASE,
    neulionOther: DEFAULT_CASE
  },
  [ISSUE_RESPONSIBILITY.OTHER]: {
    broadcastOther: { ...DEFAULT_CASE, description: '' }
  },
  [ISSUE_RESPONSIBILITY.PRODUCTIONPARTNERS]: {
    productionpartnersConnectivity: DEFAULT_CASE,
    productionpartnersEquipment: DEFAULT_CASE,
    'productionpartnersPowerFailure.optional': DEFAULT_CASE,
    productionpartnersHumanError: DEFAULT_CASE,
    productionpartnersScheduling: DEFAULT_CASE,
    productionpartnersOthers: DEFAULT_CASE
  }
} as const satisfies {
  [key in ValueOf<typeof ISSUE_RESPONSIBILITY>]: IssuesCasesMap[key]
}

export const ISSUE_FORM_LABELS = {
  ISSUE_TIME: 'Issue Time',
  LOSS_OF_GAME_PLAY: 'Loss of Game Play (minutes)',
  ISSUE_SCOPE: 'Issue Scope',
  DESCRIPTION: 'Description'
} as const

export const CASES_LABEL_MAP = {
  lateStreamStart: 'Late Stream Start',
  lateSlateDrop: 'Late Slate Drop',
  incorrectEncoder: 'Incorrect Encoder',
  incorrectSlate: 'Incorrect Slate',
  EEOther: 'Other',

  hitOnPictures: 'Hit on Pictures',
  hitOnAudio: 'Hit on Audio',
  lossOfFeed: 'Loss of Feed',
  switchingError: 'Switching Error',
  technicalFailure: 'Technical Failure (describe)',
  encryptError: 'Encryption/Decryption Error',
  powerOutage: 'Power Outage',
  weatherIssue: 'Weather Issue',
  upconverterUnavailable: 'Upconverter Unavailable',
  MCROther: 'Other',

  noBroadcastersGFX: 'No Broadcasters GFX',
  noBroadcastersClock: 'No Broadcasters Clock',
  externalSwitchingError: 'External Switching Error',
  lowPower: 'Low Power at Uplink',
  audioHot: 'Audio Hot',
  eventCanceled: 'Event Canceled',
  weatherDelay: 'Weather Delay',
  otherDelay: 'Other Delay',
  ingestOther: 'Other',

  bmMultiview: 'BM Multiview',
  vgaMonitors: 'VGA Monitors',
  audioMonitor: 'Audio Monitor',
  kvm: 'KVM',
  bncs: 'BNCs',
  hdMonitor: 'HD Monitor',
  videoHub: 'Video Hub',
  technicalOther: 'Other',

  streamRestart: 'Stream Restart',
  encoderCrash: 'Encoder Crash',
  encoderFreeze: 'Encoder Freeze',
  studioCrash: 'Studio Crash',
  studioFreeze: 'Studio Freeze',
  windowsCrash: 'Windows Crash',
  windowsFreeze: 'Windows Freeze',
  platformIssue: 'Platform Issue',
  platformDown: 'Platform Down',
  blackMagicCard: 'Black Magic Card',
  zixiUnavailable: 'Zixi Unavailable',
  eventIDCorrupt: 'Event ID Corrupt',
  cdn: 'CDN',
  deliveryOther: 'Other',

  broadcastLossOfNetwork: 'Loss of Network',
  broadcastNetworkDrop: 'Network Drop',
  broadcastBandwidthLow: 'Bandwidth Low',
  packetLoss: 'Packet Loss',
  broadcastOther: 'Other',

  incorrectBookingLength: 'Incorrect Booking Length',
  noEventBookingMade: 'No Event Booking Made',
  incorrectAudioChannels: 'Incorrect Audio Channels',
  noUpconverterBooked: 'No Upconverter Booked',
  fiberBookingError: 'Fiber Booking Error',
  satelliteBookingError: 'Satellite Booking Error',
  incorrectEncoderBooking: 'Incorrect Encoder Booking',
  adminOther: 'Other',

  neulionLateSlateDrop: 'Late Slate Drop',
  neulionIncorrectSlate: 'Incorrect Slate',
  neulionNetworkIssue: 'Network Issue',
  neulionCablingIssue: 'Cabling Issue',
  neulionOther: 'Other',

  productionpartnersConnectivity: 'Connectivity',
  productionpartnersEquipment: 'A/V Equipment',
  productionpartnersHumanError: 'Human Error',
  productionpartnersScheduling: 'Scheduling',
  'productionpartnersPowerFailure.optional': 'Power Failure',
  productionpartnersOthers: 'Other'
} as const

export const GENERIC_INPUT_PLACEHOLDER = 'e.g 25s/5m' as const
